import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Mail, Phone, GitBranch, Send } from 'lucide-react';
import { DataTable, Column } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { PageHeader } from '@/components/common/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import { useToast } from '@/components/common/Toast';
import * as contactsService from '@/services/api/contactsService';
import type { Contact } from '@/services/api/contactsService';
import { usePermissions } from '@/hooks/usePermissions';
import SendMagnetModal from '@/features/communication/SendMagnetModal';

// Premium color mappings for contact status
const contactStatusColors = {
  COLD: 'secondary',
  WARM: 'warning',
  HOT: 'destructive',
  ACTIVE: 'active',
  ENGAGED: 'active',
  INACTIVE: 'inactive',
  MQL_SUBMITTED: 'mqlSubmitted',
} as const;

export default function ContactsPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { canCreate, canEdit, canDelete } = usePermissions('contacts');
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [magnetContact, setMagnetContact] = useState<Contact | null>(null);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactsService.getContacts({
        search: searchQuery || undefined,
        page: currentPage,
        limit: pageSize,
      });
      
      setContacts(response.data);
      setTotalItems(response.total);
      
      // Save to localStorage for ABM Journey integration
      // Transform data to include name field for consistency
      const contactsWithNames = response.data.map(c => ({
        ...c,
        name: `${c.firstName} ${c.lastName}`,
        accountId: c.accountId || c.account?.id, // Use direct field or nested
      }));
      localStorage.setItem('contacts', JSON.stringify(contactsWithNames));
      console.log('✅ Saved contacts to localStorage:', contactsWithNames.length);
    } catch (error: any) {
      console.error('Error fetching contacts:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to load contacts',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [searchQuery, currentPage, pageSize]);

  const handleDelete = async () => {
    if (!selectedContact) return;

    try {
      await contactsService.deleteContact(selectedContact.id);
      
      addToast({
        type: 'success',
        title: 'Contact Deleted',
        description: `${selectedContact.firstName} ${selectedContact.lastName} has been deleted.`,
      });
      
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchContacts();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete contact',
      });
    }
  };

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      accessor: 'firstName',
      sortable: true,
      width: '20%',
      render: (_, contact) => (
        <div className="font-medium">
          {contact.firstName} {contact.lastName}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      accessor: 'email',
      sortable: true,
      width: '20%',
      render: (value) => value ? (
        <a href={`mailto:${value}`} className="text-primary hover:underline flex items-center gap-2">
          <Mail className="h-3 w-3" />
          {value}
        </a>
      ) : <span className="text-muted-foreground text-xs">No email</span>,
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: 'phone',
      width: '15%',
      render: (value) => value ? (
        <span className="flex items-center gap-2">
          <Phone className="h-3 w-3" />
          {value}
        </span>
      ) : 'N/A',
    },
    {
      key: 'title',
      header: 'Title',
      accessor: 'title',
      sortable: true,
      width: '20%',
      render: (value) => value || 'N/A',
    },
    {
      key: 'department',
      header: 'Department',
      accessor: 'department',
      width: '10%',
      render: (value) => value || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      width: '10%',
      render: (value: Contact['status']) => (
        <Badge variant={contactStatusColors[value as keyof typeof contactStatusColors] || 'default'}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '13%',
      align: 'center',
      render: (_, contact) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/contacts/${contact.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/contacts/${contact.id}/edit`);
              }}
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            title="Send Magnet"
            onClick={(e) => {
              e.stopPropagation();
              setMagnetContact(contact);
            }}
          >
            <Send className="h-4 w-4 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Open ABM Journey"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/abm-journeys/${contact.id}`);
            }}
          >
            <GitBranch className="h-4 w-4 text-primary" />
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedContact(contact);
                setDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contacts"
        description="Manage your contacts and relationships"
        actions={
          canCreate ? (
            <Button onClick={() => navigate('/contacts/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          ) : undefined
        }
      />

      <SearchBar
        value={searchQuery}
        onChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1);
        }}
        placeholder="Search contacts by name or email..."
      />

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-muted-foreground">Loading contacts...</p>
        </div>
      )}

      {!loading && (
        <>
          <DataTable
            data={contacts}
            columns={columns}
            onRowClick={(contact) => navigate(`/contacts/${contact.id}`)}
          />

          {contacts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg font-medium">No contacts found</p>
              <p className="text-muted-foreground mt-1">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'Get started by creating your first contact'
                }
              </p>
              {!searchQuery && canCreate && (
                <Button className="mt-4" onClick={() => navigate('/contacts/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              )}
            </div>
          )}

          {contacts.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          )}
        </>
      )}

      <SendMagnetModal
        open={!!magnetContact}
        onClose={() => setMagnetContact(null)}
        contactId={magnetContact?.id || ''}
        contactName={magnetContact ? `${magnetContact.firstName} ${magnetContact.lastName}` : ''}
        contactEmail={magnetContact?.email}
        currentStage={magnetContact?.currentStage}
        onSent={() => fetchContacts()}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">
                {selectedContact?.firstName} {selectedContact?.lastName}
              </span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
